import { FieldValues, UseFormWatch } from "react-hook-form";

export type FormChange = {
  name?: string;
  type?: string;
  values: any;
};

const USER_EVENT_KEYS = new Set(["name", "type", "values"]);

// Stops are registered with dot paths (`pickupList.0.date`, see StopList), but
// the form rules match field names in bracket form (`pickupList[0].date`,
// `endsWith("].date")`, ...), so array indexes are reported in bracket form.
const toBracketIndexes = (name: string) =>
  name.replace(/\.(\d+)(?=\.|$)/g, "[$1]");

/**
 * Subscribes to form changes with the semantics our form rules rely on:
 * `type === "change"` only for real input events, one callback per change.
 *
 * Since react-hook-form 7.7x the raw `watch` callback doesn't guarantee that:
 * - the onChange payload `{ name, type, values }` gets merged into the internal
 *   form state, so every later `setValue` payload (`{ ...formState, name,
 *   values }`) carries a stale `type: "change"`. Rules guarded by
 *   `type === "change"` then re-trigger on their own `setValue` calls and
 *   recurse forever (DORA-381).
 * - plain `{ ...formState }` re-emissions carry the leaked `name`/`type`/
 *   `values` too, replaying the last change (e.g. twice per keystroke).
 *
 * Every genuine emission carries a freshly cloned `values` object, while a
 * replay reuses the leaked one, so replays are dropped by `values` identity.
 * Only the onChange payload consists of exactly name/type/values; anything
 * else is programmatic and gets its `type` dropped.
 */
export const watchFormChanges = <T extends FieldValues>(
  watch: UseFormWatch<T>,
  callback: (change: FormChange) => void
) => {
  const seenValues = new WeakSet<object>();
  return watch((_, payload: any) => {
    const values = payload.values;
    if (values && typeof values === "object") {
      if (seenValues.has(values)) return;
      seenValues.add(values);
    }
    const isUserEvent = Object.keys(payload).every((key) =>
      USER_EVENT_KEYS.has(key)
    );
    callback({
      name:
        typeof payload.name === "string"
          ? toBracketIndexes(payload.name)
          : payload.name,
      type: isUserEvent ? payload.type : undefined,
      values,
    });
  });
};
